import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Keeps a tab-switcher's active tab in sync with a `?tab=` URL param (or a custom `paramName`) —
 * deep-linkable and survives a page refresh. Reads the initial tab from the URL on mount, re-syncs
 * if the URL changes underneath the component (back/forward nav, an external link landing here
 * with a different tab), and writes back to the URL (merged with whatever other params are
 * already there, replacing history rather than pushing) whenever the tab changes.
 */
export function useTabQueryParam<T extends string>(
  validTabs: readonly T[],
  defaultTab: T,
  paramName = 'tab',
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams()

  const fromUrl = useCallback((): T => {
    const requested = searchParams.get(paramName)
    return requested && (validTabs as readonly string[]).includes(requested)
      ? (requested as T)
      : defaultTab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, paramName])

  const [tab, setTabState] = useState<T>(fromUrl)

  useEffect(() => {
    setTabState((current) => {
      const next = fromUrl()
      return next !== current ? next : current
    })
  }, [fromUrl])

  const setTab = useCallback(
    (next: T) => {
      setTabState(next)
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          params.set(paramName, next)
          return params
        },
        { replace: true },
      )
    },
    [paramName, setSearchParams],
  )

  return [tab, setTab]
}
